<script lang="ts" setup>
  import { computed, ref, useCssModule, watch } from 'vue';

  defineOptions({
    name: 'll-badge',
  });

  const classes = useCssModule();

  export interface BadgeProps {
    /**
     * If true, the badge will animate when the content changes
     */
    animate?: boolean;

    /**
     * Color of the badge
     * Options: 'red' or 'blue'
     */
    color?: 'red' | 'blue';

    /**
     * The badge's content
     */
    content?: number | string;

    /**
     * Shows a gray badge with content
     */
    isDisabled?: boolean;

    /**
     * The max value of the `content` prop. If the value is greater than this, the badge will show the max value and a `+` sign.
     */
    max?: number | string;

    /**
     * Offset to adjust the badge's position. It does not apply to inline position badges.
     */
    offset?: {
      top?: number;
      right?: number;
    };

    /**
     * Badge position. Options:
     *
     * - inline: shows the badge directly next to the content (on the right), with a left margin of 6px
     * - top-right: will overlap the badge over whatever is passed in the default slot
     */
    position?: 'top-right' | 'inline';

    /**
     * The badge will be hidden if the content is `0` unless this prop is set to true.
     */
    showZero?: boolean;

    /**
     * Badge variant
     */
    variant?: 'dot' | 'standard';
  }

  const props = withDefaults(defineProps<BadgeProps>(), {
    animate: false,
    color: 'red',
    content: undefined,
    isDisabled: false,
    max: 99,
    position: 'top-right',
    offset: () => ({
      top: undefined,
      right: undefined,
    }),
    showZero: false,
    variant: 'standard',
  });

  const computedContent = computed(() => {
    const numericContent = parseInt(props.content as string, 10);
    const numericMax = parseInt(props.max as string, 10);
    // We want to compare the values, not the types, so it's okay to use `!=` here. i.e. if '23' = 23, then we know
    // the props are numbers, and we can compare the values. If you pass '23foobar' parseInt will return still return 23
    // but this check will fail like we want it to.
    // eslint-disable-next-line eqeqeq
    if (numericContent != props.content || numericMax != props.max) {
      return props.content;
    }

    return numericContent > numericMax ? `${numericMax}+` : numericContent;
  });

  const formatOffsetStyleValue = (value: number) => (value === 0 ? '0' : `${value}px`);

  const computedOffsetStyle = computed(() => ({
    top: props.offset.top !== undefined ? formatOffsetStyleValue(props.offset.top) : undefined,
    right: props.offset.right !== undefined ? formatOffsetStyleValue(props.offset.right) : undefined,
  }));

  const playAnimation = ref(false);

  watch(
    () => props.content,
    (value, oldValue) => {
      if (!props.animate) {
        return;
      }

      if (oldValue !== value) {
        playAnimation.value = true;

        setTimeout(() => {
          playAnimation.value = false;
        }, 1000);
      }
    },
  );
</script>

<template>
  <span class="stash-badge relative inline-block" data-test="stash-badge">
    <slot />
    <span
      v-show="props.variant === 'dot' || computedContent || (computedContent === 0 && props.showZero)"
      data-test="stash-badge|content-wrapper"
      :class="[
        {
          [classes['top-right']]: props.position === 'top-right' && props.variant !== 'dot',
          absolute: props.variant === 'dot' || props.position === 'top-right',
          'ml-1.5': props.position === 'inline',
        },
      ]"
      :style="props.variant === 'dot' || props.position === 'top-right' ? computedOffsetStyle : ''"
    >
      <span
        data-test="stash-badge|content"
        class="inline-block whitespace-nowrap rounded-full text-center text-[10px] font-semibold leading-4 text-white"
        :class="[
          {
            'animate-grow': playAnimation,
            'bg-red-500': props.color === 'red' && !props.isDisabled,
            'bg-blue-500': props.color === 'blue' && !props.isDisabled,
            absolute: props.variant === 'dot',
            'bg-ice-200 text-white': props.isDisabled,
            [classes['badge-dot']]: props.variant === 'dot',
            [classes['badge-standard']]: props.variant === 'standard',
            'px-1.5': props.variant !== 'dot',
          },
        ]"
      >
        {{ props.variant === 'dot' ? null : computedContent }}
      </span>
    </span>
  </span>
</template>

<style module>
  @layer utilities {
    .badge-standard {
      height: 15px;
      vertical-align: middle;
    }

    .badge-dot {
      border: 2px solid var(--color-ice-100);
      height: 10px;
      right: 0;
      width: 10px;
    }

    .top-right {
      top: 0;
      right: 0;
      transform: translateX(50%) translateY(-48%);
    }
  }
</style>
